import { css } from "@emotion/css";
import { createContext, type ReactNode } from "react";
import { RadioGroupProps as RadioGroupPropsRAC } from "react-aria-components";

import { _RadioGroup } from "./assets/_RadioGroup";
import { _Text } from "../../../_assets/_Text";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../../_assets/input-wrapper/FormLabelRAC";

export interface RadioGroupProps extends Omit<RadioGroupPropsRAC, "children"> {
  children?: React.ReactNode;
  label?: string;
  info?: ReactNode;
  description?: string;
  errors?: FieldErrorsProps["children"];
  isCard?: boolean | undefined;
  stretchChildren?: boolean;
  testID?: string;
}

interface RadioGroupContextType {
  isCard?: boolean;
}

export const RadioGroupContext = createContext<RadioGroupContextType>({
  isCard: false,
});

export function RadioGroup(props: RadioGroupProps) {
  const {
    label,
    info,
    description,
    errors,
    children,
    stretchChildren = false,
    testID,
    ...radioGroupProps
  } = props;

  return (
    <RadioGroupContext.Provider value={{ isCard: props.isCard }}>
      <_RadioGroup {...radioGroupProps} data-testid={testID}>
        {props.label && (
          <FormLabelRAC isRequired={props.isRequired} info={props.info}>
            {props.label}
          </FormLabelRAC>
        )}
        {props.orientation === "horizontal" ? (
          <div
            className={css(
              styles.horizontal,
              stretchChildren && styles.stretchChildren
            )}
          >
            {children}
          </div>
        ) : (
          children
        )}
        {description && (
          <_Text slot="description" style="input/helper" color="text-weak">
            {description}
          </_Text>
        )}
        <FieldErrors>{errors}</FieldErrors>
      </_RadioGroup>
    </RadioGroupContext.Provider>
  );
}

// RAC places the radio buttons in line with the label when using horizontal so we need custom styling
const styles = {
  horizontal: css({
    display: "flex",
    gap: "var(--space-6)",
    width: "fit-content",
  }),
  stretchChildren: css({
    width: "100%",
    gap: "var(--space-2)",
    "& > *": {
      flex: 1,
    },
  }),
};
